/**
 * [ADR-069 / F4.4 #890] Mapa único "tela → permissões" — fonte da verdade para a visão
 * "Por tela" do editor de perfis, `nav-config.ts` (menu) e as rotas (`RequerPermissaoRota` /
 * `GuardDirecaoTitulos`). Antes da F4.4, `nav-config.ts` carregava os códigos de permissão
 * literalmente em cada item — F4.3 introduziu essa duplicação; este arquivo a elimina.
 *
 * Cada `TelaAcesso` descreve UMA tela operacional (não necessariamente 1:1 com uma rota —
 * ex.: Contas a Pagar/Receber dividem a mesma rota `/financeiro/titulos` por `?direcao=`):
 *
 *   - `leitura`  — códigos que liberam VER/ENTRAR na tela (OR — basta ter um). É o que
 *                  `nav-config.ts`/`RequerPermissaoRota` consomem.
 *   - `acoes`    — permissões adicionais, toggle filho da tela na árvore "Por tela" do editor
 *                  (ex.: excluir, baixar, exportar). Não afetam se a tela aparece no menu.
 *   - `depende`  — permissões de LEITURA de apoio (pickers/dropdowns de outra tela) que a
 *                  tela precisa por baixo dos panos. NUNCA um código de escrita — um teste
 *                  em `telas-acesso.spec.ts` garante isso pra todo o mapa.
 *   - `acoesSemControle` — ações que a tela oferece na UI mas que o catálogo RBAC ainda não
 *                  cobre (linha desabilitada na árvore, com o motivo). Ver Boards "Recursos
 *                  faltantes no catálogo RBAC" (filha da #890) para o que falta criar.
 *
 * Uma permissão pode aparecer em mais de uma tela (ex.: `fin.contrato:*` em Contas a Pagar
 * E Contas a Receber — é genuinamente uma feature compartilhada, ambas direções criam
 * contrato recorrente). O editor detecta isso varrendo o mapa, sem precisar de um campo
 * "compartilhada" — ver `telasQueUsam()`.
 *
 * `TELAS_SEM_CONTROLE` são NÓS da árvore sem nenhum código de catálogo por trás — aparecem
 * desabilitados no editor. [Regra do menu global, #890 item 4, 2026-09-28] Desde que
 * Automações e Respostas rápidas ganharam recurso próprio, `TELAS_SEM_CONTROLE` só cobre
 * telas que NÃO são item de menu independente (abas/seções dentro de uma tela já controlada
 * — Categorias e Tipos de fornecedor são tabs de Catálogo/Fornecedores, não rotas próprias).
 * Todo ITEM DE MENU precisa estar em `TELAS_ACESSO` ou em `TELAS_GLOBAIS` — nunca em
 * `TELAS_SEM_CONTROLE` — o teste de cobertura do menu (`nav-config.spec.ts`) trava isso.
 * Diferente de `CODIGOS_SEM_TELA`: códigos que EXISTEM no catálogo mas não têm tela nenhuma
 * hoje (nenhuma rota do frontend os usa) — não aparecem na árvore, só existem para o teste de
 * cobertura do CATÁLOGO não falhar em cima deles.
 */

export interface AcaoSemControle {
  label: string;
  motivo: string;
}

export interface TelaAcesso {
  key: string;
  label: string;
  grupo: string;
  leitura: string[];
  acoes: string[];
  depende: string[];
  /** Rótulo específico desta tela para um código de `acoes` (em vez da descrição genérica do catálogo). */
  acaoLabels?: Record<string, string>;
  acoesSemControle?: AcaoSemControle[];
}

export interface TelaSemControle {
  key: string;
  label: string;
  grupo: string;
  motivo: string;
}

export const TELAS_ACESSO: readonly TelaAcesso[] = [
  // ----- Comercial ------------------------------------------------------------
  {
    key: 'com-inbox',
    label: 'Inbox',
    grupo: 'Comercial',
    leitura: ['inbox.conversa:ler'],
    acoes: [
      'inbox.conversa:responder', 'inbox.conversa:transferir', 'inbox.conversa:resolver',
      // [Fatia 0/1 Inbox/CRM/Atendimento, 2026-09-30] Extração de dados via IA + consumo/quota —
      // recursos novos do diagnóstico §1.1, ações da mesma tela de conversa.
      'inbox.ia_extracao:ler', 'inbox.ia_extracao:executar', 'inbox.ia_extracao:confirmar',
      'inbox.ia_consumo:ler',
    ],
    depende: [],
  },
  {
    key: 'com-crm',
    label: 'CRM',
    grupo: 'Comercial',
    leitura: ['crm.negocio:ler'],
    // crm.dashboard e crm.orcamento não têm tela própria — dashboard é seção da mesma
    // página; orçamento é aberto a partir do negócio (OrcamentoBridgePage, sub-rota sem
    // item de menu, mesma convenção de ":id" fora do menu que RequerPermissaoRota já assume).
    acoes: [
      'crm.negocio:criar', 'crm.negocio:editar', 'crm.negocio:mover', 'crm.negocio:excluir', 'crm.negocio:exportar',
      // [Fatia 0/1 Inbox/CRM/Atendimento — Decisão 6] Ação própria, restrita a Gestor/Admin.
      'crm.negocio:transferir_estabelecimento',
      'crm.dashboard:ler',
      'crm.orcamento:ler', 'crm.orcamento:criar', 'crm.orcamento:editar', 'crm.orcamento:enviar',
      'crm.orcamento:gerar_os', 'crm.orcamento:ver_custo',
      // [Fatia 0/1 — Decisão 8] Contato/anotação são recursos próprios (tenant-wide), mas
      // vivem na mesma tela de CRM (drawer do negócio, ficha do contato).
      'crm.contato:ler', 'crm.contato:criar', 'crm.contato:editar',
      'crm.anotacao:ler', 'crm.anotacao:criar', 'crm.anotacao:editar', 'crm.anotacao:excluir',
    ],
    acaoLabels: {
      'crm.negocio:transferir_estabelecimento': 'Transferir negócio entre unidades',
    },
    depende: [],
  },
  {
    key: 'com-cmp',
    label: 'Campanhas',
    grupo: 'Comercial',
    leitura: ['campanha:ler'],
    acoes: ['campanha:criar', 'campanha:disparar', 'campanha:excluir', 'campanha:exportar_optout'],
    // [#890 item 1] Campanhas de WhatsApp usam templates HSM aprovados — leitura de apoio.
    depende: ['atendimento.template_whatsapp:ler'],
  },

  // ----- Agenda -----------------------------------------------------------------
  {
    key: 'agenda',
    label: 'Agenda',
    grupo: 'Agenda',
    leitura: ['agenda.evento:ler'],
    acoes: ['agenda.evento:criar', 'agenda.evento:editar', 'agenda.evento:cancelar'],
    depende: [],
  },

  // ----- Catálogo -----------------------------------------------------------------
  {
    key: 'cat-itens',
    label: 'Itens & Categorias',
    grupo: 'Catálogo',
    leitura: ['catalogo.item:ler'],
    acoes: [
      'catalogo.item:criar', 'catalogo.item:editar', 'catalogo.item:inativar', 'catalogo.item:importar',
      'catalogo.item:excluir',
      'catalogo.preco:ler', 'catalogo.preco:editar', 'catalogo.preco:ver_custo',
      // [ADR-069 §13.31 Fatia Campanhas/Catálogo/Agenda #891/#923, PR #850 complemento 2]
      // `catalogo.categoria` cobre a aba "Categorias" desta mesma tela — mesmo padrão de
      // `catalogo.preco` acima (sub-recurso, não tela própria; "Categorias" é aba, não item
      // de menu independente, ver TELAS_SEM_CONTROLE no topo do arquivo).
      'catalogo.categoria:ler', 'catalogo.categoria:criar', 'catalogo.categoria:editar',
      'catalogo.categoria:inativar', 'catalogo.categoria:excluir',
    ],
    depende: [],
  },
  {
    key: 'cat-fornecedores',
    label: 'Fornecedores',
    grupo: 'Catálogo',
    // [Decisão do Filipe, 2026-10-02 — revoga a Decisão 1 do ADR-069 §13.31] A tela de gestão
    // passa a exigir `catalogo.fornecedor:ler` (menu, rota e árvore do editor) — não mais
    // `parte:ler`. `FornecedorController`/`ItemCatalogoFornecedorController` já usam
    // `catalogo.fornecedor:*` no backend desde a PR #850 original; só o frontend ficava
    // desalinhado. O SELETOR de fornecedor do financeiro (picker em Contas a Pagar/Receber)
    // continua em `parte:consultar` — é outra tela, outro uso (lookup, não gestão), não mexido
    // aqui (ver `depende: ['parte:consultar', ...]` em fin-pagar/fin-receber).
    leitura: ['catalogo.fornecedor:ler'],
    acoes: [
      'parte:editar', 'parte:excluir', 'parte:exportar', 'parte:ver_sensivel',
      // [ADR-069 §13.31, PR #850 complemento 2] `catalogo.tipo_fornecedor` cobre a aba "Tipos
      // de fornecedor" desta mesma tela — "Tipos de fornecedor" é aba, não item de menu
      // independente (ver TELAS_SEM_CONTROLE no topo do arquivo).
      'catalogo.tipo_fornecedor:ler', 'catalogo.tipo_fornecedor:gerenciar',
    ],
    acaoLabels: {
      'catalogo.tipo_fornecedor:gerenciar': 'Gerenciar tipos de fornecedor',
    },
    depende: [],
  },

  // ----- Financeiro (MOD-FIN) ------------------------------------------------------
  {
    key: 'fin-contas',
    label: 'Contas',
    grupo: 'Financeiro',
    leitura: ['fin.conta_bancaria:ler'],
    acoes: ['fin.conta_bancaria:criar', 'fin.conta_bancaria:editar', 'fin.conta_bancaria:ver_saldo', 'fin.conta_bancaria:conciliar'],
    depende: [],
  },
  {
    key: 'fin-conciliacao',
    label: 'Conciliação bancária',
    grupo: 'Financeiro',
    leitura: ['fin.conciliacao:ler'],
    acoes: ['fin.conciliacao:importar', 'fin.conciliacao:conciliar', 'fin.conciliacao:ver_sensivel'],
    acaoLabels: {
      'fin.conciliacao:ver_sensivel': 'Ver CPF/CNPJ do extrato',
    },
    depende: ['fin.conta_bancaria:ler'],
  },
  {
    // [AB#1356 / ADR-085] Regras e apelidos que reconhecem lançamentos do extrato.
    key: 'fin-conciliacao-regras',
    label: 'Regras de conciliação',
    grupo: 'Financeiro',
    leitura: ['fin.conciliacao_regra:ler'],
    acoes: ['fin.conciliacao_regra:gerenciar'],
    depende: ['fin.conciliacao:ler'],
  },
  {
    // [ADR-074 Adendo 3 / AB#1107] Módulo próprio, separado de Conciliação — abas Boletos e
    // Retornos nesta fatia; Remessas/Instruções/Configuração entram na Fase 2 (AB#1060/1062/1065).
    key: 'fin-cobranca',
    label: 'Cobrança',
    grupo: 'Financeiro',
    leitura: ['fin.cobranca:ler'],
    // `emitir`/`configurar` ainda sem rota (Fase 2 — Remessas/Configuração, AB#1062/1060):
    // já no catálogo e nesta tela pra o Admin poder conceder com antecedência nos perfis.
    acoes: [
      'fin.cobranca:importar',
      'fin.cobranca:instruir',
      'fin.cobranca:emitir',
      'fin.cobranca:bordero_montar',
      'fin.cobranca:bordero_aprovar',
      'fin.cobranca:enviar',
      'fin.cobranca:configurar',
      'fin.cobranca:ver_sensivel',
    ],
    acaoLabels: {
      'fin.cobranca:enviar': 'Enviar cobrança ao cliente (e-mail/WhatsApp)',
      'fin.cobranca:configurar': 'Configurar cobrança e mensagens ao cliente',
      'fin.cobranca:bordero_montar': 'Montar lotes de cobrança',
      'fin.cobranca:bordero_aprovar': 'Aprovar lotes de cobrança',
      'fin.cobranca:ver_sensivel': 'Ver CPF/CNPJ do cliente',
    },
    depende: ['fin.conta_bancaria:ler'],
  },
  {
    key: 'fin-pagar',
    label: 'Contas a Pagar',
    grupo: 'Financeiro',
    leitura: ['fin.titulo_pagar:ler'],
    acoes: [
      'fin.titulo_pagar:criar', 'fin.titulo_pagar:editar', 'fin.titulo_pagar:excluir', 'fin.titulo_pagar:baixar',
      'fin.titulo_pagar:estornar_baixa', 'fin.titulo_pagar:prorrogar', 'fin.titulo_pagar:exportar',
      // Sub-feature exclusiva de Pagar: fila de classificação de nota fiscal + config SEFAZ.
      'fin.documento_fiscal:ler', 'fin.documento_fiscal:importar', 'fin.documento_fiscal:classificar', 'fin.documento_fiscal:excluir',
      'fin.sefaz:gerenciar',
      // Contrato recorrente — feature compartilhada com Contas a Receber (mesmo POST
      // /financeiro/contratos, campo `direcao`; ver também em fin-receber).
      'fin.contrato:ler', 'fin.contrato:criar', 'fin.contrato:editar', 'fin.contrato:gerar_ciclo',
      'fin.contrato:reajustar', 'fin.contrato:encerrar', 'fin.contrato:distratar',
      // [Decisão 1] Sai de `depende` (era escrita, não podia estar lá) e vira ação com
      // rótulo específico desta tela.
      // [ADR-069 §7/§13.15 F2 #925 #891 — Decisão B, 2026-09-29] `parte:criar` → `fin.fornecedor:criar`:
      // código restrito ao MOD-FIN, não mais a amplitude de `parte:criar` (CRM/Inbox).
      'fin.fornecedor:criar',
      // [F1 #891, 2026-09-28] Adiantamento ganhou recurso próprio no catálogo — sai de
      // `acoesSemControle`. Só PAGAR (`TituloDrawer.tsx`: `SecaoAdiantamento`/compensação são
      // exclusivas de título PAGAR com saldo) — não entra em fin-receber.
      'fin.adiantamento:ler', 'fin.adiantamento:compensar', 'fin.adiantamento:estornar',
      'fin.adiantamento:encerrar', 'fin.adiantamento:ajustar', 'fin.adiantamento:prorrogar',
        'fiscal.documento:liberar',
  ],
    acaoLabels: {
      'fin.fornecedor:criar': 'Cadastrar fornecedor',
      'fin.adiantamento:ler': 'Ver adiantamentos',
      'fin.adiantamento:compensar': 'Compensar adiantamento',
      'fin.adiantamento:estornar': 'Estornar aplicação de adiantamento',
      'fin.adiantamento:encerrar': 'Encerrar adiantamento',
      'fin.adiantamento:ajustar': 'Lançar ajuste de adiantamento',
      'fin.adiantamento:prorrogar': 'Prorrogar prestação de adiantamento',
    },
    // Leitura de apoio pros pickers do formulário (fornecedor, plano de contas, centro de
    // custo, conta bancária p/ baixa) — achado lendo TitulosPage.tsx + BuscaParte.tsx.
    // [ADR-069 §7/§13.15 F2 #925 #891, 2026-09-29] `:ler` → `:consultar`: seletor não precisa
    // abrir a tela de gestão do recurso de apoio, só ler pra escolher.
    depende: ['parte:consultar', 'fin.plano_contas:consultar', 'fin.centro_custo:consultar', 'fin.conta_bancaria:consultar'],
  },
  {
    // [AB#1215, ADR-082 D7] Agenda de pagamentos (a pagar). A leitura é a MESMA de Contas a Pagar (`fin.titulo_pagar:ler`): a
    // agenda é só outra visão dos mesmos títulos, no mesmo escopo de unidade — não ganhou chave de leitura própria. Reprogramar a
    // data usa `fin.titulo_pagar:editar`; só a liberação tem chave própria.
    key: 'fin-agenda-pagamentos',
    label: 'Agenda de pagamentos',
    grupo: 'Financeiro',
    leitura: ['fin.titulo_pagar:ler'],
    acoes: ['fin.pagamentos:liberar'],
    acaoLabels: {
      'fin.pagamentos:liberar': 'Liberar pagamentos',
    },
    depende: [],
  },
  {
    // [AB#1155, ADR-082 D8] Remessas de pagamento (CNAB 240). Leitura = a de Contas a Pagar (mesmos títulos, mesmo escopo); gerar/baixar o
    // arquivo e importar o retorno = `remessa_gerar`; aprovar = `remessa_aprovar` (papéis separados).
    key: 'fin-remessas-pagamento',
    label: 'Lotes de pagamento',
    grupo: 'Financeiro',
    leitura: ['fin.titulo_pagar:ler'],
    acoes: ['fin.pagamentos:remessa_gerar', 'fin.pagamentos:remessa_aprovar'],
    acaoLabels: {
      'fin.pagamentos:remessa_gerar': 'Gerar lotes de pagamento, baixar o arquivo e importar o retorno',
      'fin.pagamentos:remessa_aprovar': 'Aprovar lotes de pagamento',
    },
    depende: [],
  },
  {
    key: 'fin-receber',
    label: 'Contas a Receber',
    grupo: 'Financeiro',
    leitura: ['fin.titulo_receber:ler'],
    acoes: [
      'fin.titulo_receber:criar', 'fin.titulo_receber:editar', 'fin.titulo_receber:excluir', 'fin.titulo_receber:baixar',
      'fin.titulo_receber:estornar_baixa', 'fin.titulo_receber:prorrogar', 'fin.titulo_receber:exportar',
      // Mesmo bloco de fin-pagar — feature de contrato recorrente é das duas direções.
      'fin.contrato:ler', 'fin.contrato:criar', 'fin.contrato:editar', 'fin.contrato:gerar_ciclo',
      'fin.contrato:reajustar', 'fin.contrato:encerrar', 'fin.contrato:distratar',
    ],
    // [ADR-069 §7/§13.15 F2 #925 #891, 2026-09-29] `:ler` → `:consultar`, mesma razão de fin-pagar.
    depende: ['parte:consultar', 'fin.plano_contas:consultar', 'fin.centro_custo:consultar', 'fin.conta_bancaria:consultar'],
  },
  {
    // [US 974 UI] Parâmetros do estabelecimento: competência do título de nota de entrada (fin.config) e corte fiscal (fiscal.config).
    // [ADR-061 US6.1] + contabilização por livro (cont.livro) — mesma tela, nova seção.
    key: 'cont-parametros',
    label: 'Parâmetros',
    grupo: 'Contabilidade',
    leitura: ['fin.config:ler', 'fiscal.config:ler', 'cont.livro:ler'],
    acoes: ['fin.config:editar', 'fiscal.config:editar', 'cont.livro:editar'],
    depende: [],
  },
  {
    // [US 974 UI / US 981] Fechamento de períodos por estabelecimento: camada fiscal (ADR-073 §11) e financeira (ADR-061 D8).
    key: 'cont-fech-fiscal',
    label: 'Fechamento de períodos',
    grupo: 'Contabilidade',
    leitura: ['fiscal.periodo:ler', 'fin.periodo:ler'],
    acoes: ['fiscal.periodo:fechar', 'fiscal.periodo:reabrir', 'fin.periodo:fechar', 'fin.periodo:reabrir', 'fiscal.config:ler'],
    depende: [],
  },
  {
    // [ADR-061 US8.2/US8.3] Balancete e Razão — demonstrativos de leitura (mod_09), com exportação
    // PDF/XLSX (`cont.demonstrativo:exportar`).
    key: 'cont-balancete',
    label: 'Balancete',
    grupo: 'Contabilidade',
    leitura: ['cont.demonstrativo:ler'],
    acoes: ['cont.demonstrativo:exportar'],
    depende: [],
  },
  {
    key: 'cont-razao',
    label: 'Razão',
    grupo: 'Contabilidade',
    leitura: ['cont.demonstrativo:ler'],
    acoes: ['cont.demonstrativo:exportar'],
    depende: [],
  },
  {
    // [ADR-061 AB#1046] Livro Diário — demonstrativo de leitura, mesma permissão do Razão/Balancete.
    key: 'cont-diario',
    label: 'Diário',
    grupo: 'Contabilidade',
    leitura: ['cont.demonstrativo:ler'],
    acoes: ['cont.demonstrativo:exportar'],
    depende: [],
  },
  {
    // [ADR-061 D3, F1 US3] Plano contábil — tela nova em mod_09, distinta de "Plano de
    // contas" (fin-planos, financeiro/gerencial). Sem ação de excluir no catálogo — D3/D7:
    // conta ausente na reimportação é sempre inativada, nunca excluída.
    key: 'cont-plano-contabil',
    label: 'Plano contábil',
    grupo: 'Contabilidade',
    leitura: ['cont.plano:ler'],
    acoes: ['cont.plano:criar', 'cont.plano:editar', 'cont.plano:inativar', 'cont.plano:importar'],
    depende: [],
  },
  {
    // [ADR-061 US8.4.3] DRE calculada — leitura dos demonstrativos (mesma do Balancete/Razão).
    key: 'cont-dre',
    label: 'DRE',
    grupo: 'Contabilidade',
    leitura: ['cont.demonstrativo:ler'],
    acoes: [],
    depende: [],
  },
  {
    // [ADR-061 US8.4.2] Estrutura da DRE por livro: ver (linhas + vínculo) = leitura dos demonstrativos;
    // editar a estrutura e importar vínculos têm chaves próprias (`cont.dre`).
    key: 'cont-dre-estrutura',
    label: 'Estrutura da DRE',
    grupo: 'Contabilidade',
    leitura: ['cont.demonstrativo:ler'],
    acoes: ['cont.dre:editar', 'cont.dre:importar'],
    depende: [],
  },
  {
    // [ADR-061 D6/D7, US 1045] Lançamento manual — leitura própria (`cont.lancamento:ler`,
    // também usada pelo link "Manual · nº X" do Razão); criar e estornar são ações.
    key: 'cont-lancamentos',
    label: 'Lançamentos',
    grupo: 'Contabilidade',
    leitura: ['cont.lancamento:ler'],
    acoes: ['cont.lancamento:criar', 'cont.lancamento:estornar'],
    depende: [],
  },
  {
    // [ADR-061 D2/D6, US5] De-para — mod_09, mesmo padrão de "Plano contábil" acima.
    key: 'cont-mapeamento',
    label: 'Mapeamento contábil',
    grupo: 'Contabilidade',
    leitura: ['cont.mapeamento:ler'],
    acoes: ['cont.mapeamento:editar'],
    depende: [],
  },
  {
    // [F1 #891, 2026-09-28] `criar`/`excluir`/`inativar` (toggle) ganharam ação no catálogo —
    // saem de `acoesSemControle` (PlanoContasPage.tsx já tinha as 3 rotas sem controle nenhum).
    key: 'fin-planos',
    label: 'Plano de contas',
    grupo: 'Contabilidade',
    leitura: ['fin.plano_contas:ler'],
    acoes: ['fin.plano_contas:criar', 'fin.plano_contas:editar', 'fin.plano_contas:inativar', 'fin.plano_contas:excluir', 'fin.plano_contas:importar'],
    depende: [],
  },
  {
    // [F1 #891, 2026-09-28] Mesma correção de fin-planos — CentroCustoPage.tsx tinha as 3
    // mesmas rotas sem controle.
    key: 'fin-centros',
    label: 'Centro de custo',
    grupo: 'Financeiro',
    leitura: ['fin.centro_custo:ler'],
    acoes: ['fin.centro_custo:criar', 'fin.centro_custo:editar', 'fin.centro_custo:inativar', 'fin.centro_custo:excluir', 'fin.centro_custo:importar'],
    depende: [],
  },

  // ----- Minha conta ---------------------------------------------------------------
  {
    // [Decisão de produto 2026-09-28 / ADR-069 §13.7] Assinatura passa a exigir permissão —
    // por padrão só o Administrador (Gestor perdeu config.assinatura:* do perfil de fábrica,
    // mas continua concedível). Meu perfil e Chamados continuam sem controle (globais, ver
    // TELAS_GLOBAIS) — só Assinatura sai da área "sempre visível" de Minha conta.
    key: 'mc-ass',
    label: 'Assinatura',
    grupo: 'Minha conta',
    leitura: ['config.assinatura:ler'],
    acoes: ['config.assinatura:gerenciar'],
    depende: [],
  },

  // ----- Configurações -----------------------------------------------------------
  {
    key: 'cfg-usu',
    label: 'Usuários',
    grupo: 'Configurações',
    leitura: ['config.usuario:ler'],
    acoes: ['config.usuario:convidar', 'config.usuario:editar', 'config.usuario:inativar'],
    depende: [],
  },
  {
    key: 'cfg-perfis',
    label: 'Perfis e Acesso',
    grupo: 'Configurações',
    leitura: ['config.perfil:ler'],
    acoes: ['config.perfil:criar', 'config.perfil:editar', 'config.perfil:excluir', 'config.acesso:conceder', 'config.acesso:revogar'],
    depende: [],
  },
  {
    key: 'cfg-est',
    label: 'Estabelecimentos',
    grupo: 'Configurações',
    leitura: ['config.estabelecimento:ler'],
    acoes: ['config.estabelecimento:criar', 'config.estabelecimento:editar', 'config.estabelecimento:inativar', 'fiscal.config:editar'],
    depende: [],
  },
  {
    key: 'cfg-emp',
    label: 'Dados cadastrais',
    grupo: 'Configurações',
    leitura: ['config.empresa:ler'],
    acoes: ['config.empresa:editar'],
    depende: [],
  },
  {
    // [AB#1275] Só 1 permissão — ler e gerenciar são a mesma ação aqui, não
    // há visão "só leitura" do card (token write-only, nada pra mostrar sem
    // poder trocar). Gate de módulo (mod_11) fica no item de menu, não aqui.
    key: 'cfg-integracoes',
    label: 'Integrações',
    grupo: 'Configurações',
    leitura: ['pep.config:gerenciar'],
    acoes: [],
    depende: [],
  },

  // ----- Atendimento (config) ------------------------------------------------------
  {
    key: 'cfg-cmc',
    label: 'Caixas de entrada',
    grupo: 'Atendimento',
    // Gate atual (F4.3) já é `gerenciar`, não `ler` — mantido como está, fora do escopo
    // do levantamento da F4.4 mexer em telas que já tinham controle correto.
    leitura: ['inbox.canal:gerenciar'],
    acoes: ['inbox.canal:ler'],
    depende: [],
  },
  {
    key: 'cfg-crm',
    label: 'CRM (configuração)',
    grupo: 'Atendimento',
    leitura: ['crm.pipeline:gerenciar'],
    acoes: [],
    depende: [],
  },
  {
    // [#890 item 1, 2026-09-28] Antes "sem controle" (TELAS_SEM_CONTROLE) — ganhou recurso
    // próprio no catálogo. Cobre automacao-mensagem (eventos) E templates-hsm (mesma tela,
    // AutomacoesPage.tsx) — dois recursos do catálogo, uma tela só na UI.
    key: 'atd-automacoes',
    label: 'Automações',
    grupo: 'Atendimento',
    leitura: ['atendimento.automacao:ler'],
    // A mesma tela gerencia templates HSM (AutomacoesPage chama /automacoes/templates-hsm/*
    // — criar, sincronizar, excluir) — ação desta tela, não dependência de outra.
    acoes: [
      'atendimento.automacao:gerenciar',
      'atendimento.template_whatsapp:ler',
      'atendimento.template_whatsapp:gerenciar',
      'atendimento.template_whatsapp:excluir',
    ],
    depende: [],
  },
  {
    // [#890 item 1, 2026-09-28] Antes "sem controle" — ganhou recurso próprio (★, aceita
    // PROPRIOS: pessoal × compartilhada, ADR-045 §5).
    key: 'atd-respostas-rapidas',
    label: 'Respostas rápidas',
    grupo: 'Atendimento',
    leitura: ['atendimento.resposta_rapida:ler'],
    acoes: [
      'atendimento.resposta_rapida:criar',
      'atendimento.resposta_rapida:editar',
      'atendimento.resposta_rapida:excluir',
      'atendimento.resposta_rapida:compartilhar',
    ],
    acaoLabels: { 'atendimento.resposta_rapida:compartilhar': 'Promover/rebaixar alcance (pessoal ↔ compartilhada)' },
    depende: [],
  },

  // ----- Auditoria -----------------------------------------------------------------
  // [ADR-069 §11/§13.4 F9a/F9b #891] A aba "Negações de acesso" (`NegacoesAcessoTab.tsx`,
  // dentro de `AuditLog.tsx`) é uma TAB desta mesma tela, não uma rota própria — reusa
  // `auditoria:ler` como leitura, sem entrada própria neste mapa (mesmo critério de
  // "Categorias"/"Tipos de fornecedor" em Catálogo/Fornecedores: aba, não item de menu).
  {
    key: 'auditoria',
    label: 'Auditoria',
    grupo: 'Auditoria',
    leitura: ['auditoria:ler'],
    acoes: ['auditoria:exportar'],
    depende: [],
  },
];

/** [Decisão 2] Telas sem nenhum código de catálogo por trás — nó desabilitado no editor. */
// [ADR-069 §13.31, PR #850 complemento 2] 'cat-categorias'/'cat-tipos-fornecedor' saíram daqui —
// ganharam recurso próprio (catalogo.categoria/catalogo.tipo_fornecedor, acoes de cat-itens/
// cat-fornecedores acima). Vazio por ora; próxima tela sem controle entra aqui quando existir.
export const TELAS_SEM_CONTROLE: readonly TelaSemControle[] = [];

export interface TelaGlobal {
  key: string;
  label: string;
}

/**
 * [Princípio do Filipe, #890 item 4, 2026-09-28] As ÚNICAS telas visíveis a QUALQUER usuário
 * autenticado, sem nenhuma permissão — todo o resto do menu exige `permissao` declarada
 * aqui ou em `leituraDaTela()`. `key` casa com o `key` real do item em `nav-config.ts`
 * (`nav-config.spec.ts` varre `TENANT_NAV` e falha se um item novo não estiver nem aqui nem
 * com `permissao`). Início mostra cards filtrados por permissão internamente — a PÁGINA em si
 * é global, o conteúdo dela não.
 */
export const TELAS_GLOBAIS: readonly TelaGlobal[] = [
  { key: 'dashboard', label: 'Início' },
  { key: 'mc-perfil', label: 'Meu perfil' },
  { key: 'mc-cha', label: 'Chamados' },
  { key: 'faq', label: 'Base de conhecimento' },
];

/**
 * Códigos do catálogo que existem mas não têm tela nenhuma hoje (nenhuma rota do
 * frontend os usa) — mod_08/mod_09 sem UI, self-service ainda no `@Roles` legado
 * (`config.proposta_segmento`/`config.nota_fiscal`, ver comentário em
 * `permissoes.catalogo.ts` — `config.assinatura` SAIU daqui em 2026-09-28: ganhou tela própria,
 * `mc-ass`), telas nunca construídas (`config.alcada`, `config.credenciais`,
 * `inbox.atena_atendente`, `fin.relatorio`) e a delegação da Atena (`atena.*`, concedida por
 * perfil complementar — ADR-070 — não por uma tela operacional).
 *
 * Existe só para o teste de cobertura (`telas-acesso.spec.ts`) não falhar nestes códigos —
 * não aparece na árvore do editor.
 */
export const CODIGOS_SEM_TELA: ReadonlySet<string> = new Set([
  'config.proposta_segmento:ler', 'config.proposta_segmento:editar',
  'config.nota_fiscal:ler', 'config.nota_fiscal:editar',
  'config.alcada:ler', 'config.alcada:gerenciar',
  'config.credenciais:gerenciar',
  'inbox.atena_atendente:gerenciar',
  'fin.relatorio:ler', 'fin.relatorio:exportar',
  // [F1 #891, 2026-09-28] fin.transferencia: recurso novo, zero consumidor no frontend hoje
  // (grep em `apps/frontend/src` por `/financeiro/transferencias` não achou nada).
  'fin.transferencia:ler', 'fin.transferencia:criar', 'fin.transferencia:estornar',
  'compras.solicitacao:ler', 'compras.solicitacao:criar', 'compras.solicitacao:editar', 'compras.solicitacao:cancelar',
  'compras.cotacao:ler', 'compras.cotacao:criar', 'compras.cotacao:enviar_fornecedor', 'compras.cotacao:registrar_resposta',
  'compras.pedido:ler', 'compras.pedido:emitir', 'compras.pedido:cancelar',
  'compras.recebimento:ler', 'compras.recebimento:registrar',
  'compras.pipeline:gerenciar',
  'compras.config:gerenciar',
  'cont.periodo:fechar', 'cont.periodo:reabrir',
  'cont.integracao:exportar_dominio',
  'atena.chat:usar', 'atena.consulta_livre:usar', 'atena.escrita:usar', 'atena.insights:receber',
  'atena.auditoria:ler_conversas_de_outros', 'atena.acesso:delegar',
  // [ADR-069 §7/§13.15 F2 #925 #891 — Decisão B, 2026-09-29] `parte:criar`: órfão desde que
  // "Cadastrar fornecedor" em fin-pagar passou a usar `fin.fornecedor:criar` — nenhuma rota do
  // backend exige mais `parte:criar` (só `POST /financeiro/partes` usava, e trocou). Fica no
  // catálogo como permissão genérica "criar Parte" pra um futuro consumidor (CRM/Inbox).
  'parte:criar',
  // [Decisão do Filipe, 2026-10-02] `parte:ler`: órfão desde que `cat-fornecedores` migrou a
  // leitura pra `catalogo.fornecedor:ler` — nenhuma tela usa mais `parte:ler` diretamente (o
  // lookup do financeiro usa `parte:consultar`, código diferente). Fica no catálogo como
  // permissão genérica "ler Parte" pra um futuro consumidor.
  'parte:ler',
  // [ADR-069 §7/§13.15 F2 #925 #891, 2026-09-29] `catalogo.item:consultar`: lookup novo
  // (`GET /catalogo/itens/consultar`) pro seletor de item do orçamento (com-crm, mod_07-gated)
  // e da agenda — nenhum dos dois é tela MOD-FIN (regras 4/5 do Fatia 2 escopam só `fin-*`), e
  // nenhuma tela referencia o código em `depende` ainda. Endpoint pronto, adoção do frontend
  // fica para quando o consumidor migrar.
  'catalogo.item:consultar',
]);

const POR_KEY = new Map<string, TelaAcesso>(TELAS_ACESSO.map((t) => [t.key, t]));

export function telaPorKey(key: string): TelaAcesso {
  const t = POR_KEY.get(key);
  if (!t) throw new Error(`Tela de acesso desconhecida: ${key}`);
  return t;
}

/** Códigos que liberam ver/entrar na tela — o que `nav-config.ts`/`RequerPermissaoRota` consomem. */
export function leituraDaTela(key: string): string[] {
  return telaPorKey(key).leitura;
}

/** Todas as telas (com controle) que usam um código, em `leitura`, `acoes` ou `depende` — base pra detectar permissão compartilhada entre telas no editor. */
export function telasQueUsam(codigo: string): TelaAcesso[] {
  return TELAS_ACESSO.filter((t) => t.leitura.includes(codigo) || t.acoes.includes(codigo) || t.depende.includes(codigo));
}

/** Agrupa `TELAS_ACESSO` + `TELAS_SEM_CONTROLE` por `grupo`, na ordem de primeira aparição. */
export function telasPorGrupo(): { grupo: string; telas: TelaAcesso[]; semControle: TelaSemControle[] }[] {
  const ordem: string[] = [];
  const mapa = new Map<string, { telas: TelaAcesso[]; semControle: TelaSemControle[] }>();
  function bucket(grupo: string) {
    let b = mapa.get(grupo);
    if (!b) {
      b = { telas: [], semControle: [] };
      mapa.set(grupo, b);
      ordem.push(grupo);
    }
    return b;
  }
  for (const t of TELAS_ACESSO) bucket(t.grupo).telas.push(t);
  for (const s of TELAS_SEM_CONTROLE) bucket(s.grupo).semControle.push(s);
  return ordem.map((grupo) => ({ grupo, ...mapa.get(grupo)! }));
}
